import { c as _c } from "react/compiler-runtime";
import { ActionIcon, Alert, Button, Popover, Skeleton, TextInput, ThemeIcon, Timeline, Tooltip, useCombobox, Combobox as Combo, PillsInput, Pill, CloseButton, CheckIcon, Switch } from '@mantine/core';
import { useQuery, useMutation } from '@tanstack/react-query';
import { usePortal } from 'components/portal/portal';
import { SAvatarGroup } from 'components/portal/SAvatar';
import { DateTime as D, DateTime } from 'luxon';
import React, { ReactNode, useEffect, useState } from 'react';
import { HiCalendar, HiChevronDown, HiChevronUp, HiClock, HiEye, HiPencil, HiShare, HiTrash, HiUpload } from 'react-icons/hi';
import { entries, isEmpty, partition, pick, pipe, range, sortBy, unique } from 'remeda';
import { NotNull, notNull } from 'shared/type';
import { fmtDate } from 'shared/format';
import { compactObj, isNullOrEmpty, notNullOrEmpty } from 'shared/collection';
import { QuestionCfg, QuestionPublicationCfg, zQuestionStatus } from 'shared/forecast';
import { LabelForm, useMemoValue } from 'shared/ui';
import { match, P } from 'ts-pattern';
import { IconContext } from 'react-icons';
import { z } from 'zod';
import { questionStatusMd } from 'shared/forecast/questionStatus';
import { openConfirmModal } from '@mantine/modals';
import { DbFormSubset, dbFormSubset } from 'components/hooks/useDbForm';
import { useQueryString } from 'utils/useQueryString';
import { PublishLink, PublishStatus, usePublishedUrl } from './QuestionPublish';
import { FaGlobeAmericas } from 'react-icons/fa';
import { QuestionStatusIcon } from './QuestionStatus';
import { CardCollapsable } from 'components/Card';
import { DatePicker, TimeInput } from '@mantine/dates';
import { sb } from 'utils/db';
import { InfoLabel } from 'components/Info';
import { ResultsQuery } from 'shared/forecast/results';
import { compare } from 'semver-ts';
import { CopyLink } from 'components/form/CopyLink';
import { Publish } from '../../api/publish';
import { firstBy } from 'shared/agg';
import { asyncMap } from 'modern-async';
import { useForecastNotes, UseForecastNotes } from './useForecastNotes';
import { useForecasts, UseForecasts } from './useForecasts';
import { useQuestionForm, useQuestionStats } from './useQuestions';
import { useQuestionContext } from './questionComponentTypes';
const usePublish = options => {
  const $ = _c(15);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = Publish.client.muteProps("publish");
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = options?.onSuccess;
  const t2 = options?.onError;
  let t3;
  if ($[1] !== t1 || $[2] !== t2) {
    t3 = {
      ...t0,
      onSuccess: t1,
      onError: t2
    };
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const publishMutation = useMutation(t3);
  let t4;
  if ($[4] !== publishMutation) {
    t4 = (question_id, ts) => publishMutation.mutate({
      question_id,
      publish: true,
      ts
    });
    $[4] = publishMutation;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const publish = t4;
  let t5;
  if ($[6] !== publishMutation) {
    t5 = (question_id_0, ts_0) => publishMutation.mutate({
      question_id: question_id_0,
      publish: false,
      ts: ts_0
    });
    $[6] = publishMutation;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const unpublish = t5;
  let t6;
  if ($[8] !== publish || $[9] !== publishMutation.error || $[10] !== publishMutation.isError || $[11] !== publishMutation.isPending || $[12] !== publishMutation.reset || $[13] !== unpublish) {
    t6 = {
      publish,
      unpublish,
      isPending: publishMutation.isPending,
      isError: publishMutation.isError,
      error: publishMutation.error,
      reset: publishMutation.reset
    };
    $[8] = publish;
    $[9] = publishMutation.error;
    $[10] = publishMutation.isError;
    $[11] = publishMutation.isPending;
    $[12] = publishMutation.reset;
    $[13] = unpublish;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};
const usePublishBatch = options => {
  const $ = _c(12);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = Publish.client.muteProps("publish");
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = options?.onSuccess;
  const t2 = options?.onError;
  let t3;
  if ($[1] !== t1 || $[2] !== t2) {
    t3 = {
      ...t0,
      onSuccess: t1,
      onError: t2
    };
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const publishMutation = useMutation(t3);
  let t4;
  if ($[4] !== publishMutation) {
    t4 = async (question_id, timestamps) => {
      if (timestamps.length === 0) {
        return;
      }
      await asyncMap(timestamps, async ts => publishMutation.mutate({
        question_id,
        publish: true,
        ts
      }));
    };
    $[4] = publishMutation;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const publishBatch = t4;
  let t5;
  if ($[6] !== publishBatch || $[7] !== publishMutation.error || $[8] !== publishMutation.isError || $[9] !== publishMutation.isPending || $[10] !== publishMutation.reset) {
    t5 = {
      publishBatch,
      isPending: publishMutation.isPending,
      isError: publishMutation.isError,
      error: publishMutation.error,
      reset: publishMutation.reset
    };
    $[6] = publishBatch;
    $[7] = publishMutation.error;
    $[8] = publishMutation.isError;
    $[9] = publishMutation.isPending;
    $[10] = publishMutation.reset;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
};
const PublishButton = t0 => {
  const $ = _c(27);
  let buttonProps;
  let onError;
  let onSuccess;
  let question_id;
  let t1;
  let ts;
  if ($[0] !== t0) {
    ({
      question_id,
      ts,
      onSuccess,
      onError,
      children: t1,
      ...buttonProps
    } = t0);
    $[0] = t0;
    $[1] = buttonProps;
    $[2] = onError;
    $[3] = onSuccess;
    $[4] = question_id;
    $[5] = t1;
    $[6] = ts;
  } else {
    buttonProps = $[1];
    onError = $[2];
    onSuccess = $[3];
    question_id = $[4];
    t1 = $[5];
    ts = $[6];
  }
  const children = t1 === undefined ? "Publish" : t1;
  let t2;
  if ($[7] !== onError || $[8] !== onSuccess) {
    t2 = {
      onSuccess,
      onError
    };
    $[7] = onError;
    $[8] = onSuccess;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const {
    publish,
    isPending,
    isError,
    error,
    reset
  } = usePublish(t2);
  let t3;
  if ($[10] !== publish || $[11] !== question_id || $[12] !== ts) {
    t3 = () => publish(question_id, ts);
    $[10] = publish;
    $[11] = question_id;
    $[12] = ts;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const handlePublish = t3;
  let t4;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <FaGlobeAmericas />;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== buttonProps || $[16] !== children || $[17] !== handlePublish || $[18] !== isPending) {
    t5 = <Button leftSection={t4} loading={isPending} onClick={handlePublish} {...buttonProps}>{children}</Button>;
    $[15] = buttonProps;
    $[16] = children;
    $[17] = handlePublish;
    $[18] = isPending;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== error || $[21] !== isError || $[22] !== reset) {
    t6 = isError && <Alert color="red" title="Failed to publish question" onClose={reset}>{error instanceof Error ? error.message : "An unexpected error occurred"}</Alert>;
    $[20] = error;
    $[21] = isError;
    $[22] = reset;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== t5 || $[25] !== t6) {
    t7 = <div className="flex flex-col gap-2">{t5}{t6}</div>;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  return t7;
};
const zTimelineRowCommon = z.object({
  ts: z.coerce.number()
});
const zTimelineCfgWithForecasts = z.object({
  num_forecasts: z.number(),
  profiles: z.array(z.string())
});
const zPublishTypes = {
  publish: zTimelineRowCommon.extend({
    type: z.literal('publish'),
    cfg: zTimelineCfgWithForecasts
  }),
  day: zTimelineRowCommon.extend({
    type: z.literal('day'),
    cfg: zTimelineCfgWithForecasts
  }),
  status: zTimelineRowCommon.extend({
    type: z.literal('status'),
    cfg: z.object({
      status: zQuestionStatus
    })
  })
};
const zTimelineRow = z.discriminatedUnion('type', [zPublishTypes.publish, zPublishTypes.status, zPublishTypes.day]);
export const TsLatest = 'latest';
type TimelineRow = z.infer<typeof zTimelineRow>;
type TimelineItemCommon = {
  selected: boolean;
  key: string;
} & QuestionPublicationCfg;
type TimelineStatus = Extract<TimelineRow, {
  type: 'status';
}> & TimelineItemCommon & {
  date: D;
};
type TimelineDayOrPublish = Extract<TimelineRow, {
  type: 'day' | 'publish';
}> & TimelineItemCommon & {
  date: D;
};
type TimelineCustom = {
  type: 'custom';
  date: D;
  ts: number;
} & TimelineItemCommon;
const isDayOrPublish = (r: {
  type: string;
}): r is TimelineDayOrPublish => r.type == 'day' || r.type == 'publish';
type TimelineLatest = {
  type: 'latest';
  noteStats: {
    total: number;
    withNotes: number;
    notesPublished: number;
  };
} & TimelineItemCommon & {
  ts: number;
};
type TimelineItem = TimelineStatus | TimelineDayOrPublish | TimelineLatest | TimelineCustom;
export const useTimeline = () => {
  const $ = _c(56);
  const {
    question_id,
    readonly
  } = useQuestionContext();
  const id = question_id;
  let t0;
  if ($[0] !== question_id) {
    t0 = {
      question_id,
      pick: _temp
    };
    $[0] = question_id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    form: qForm,
    question: questionStored
  } = useQuestionForm(t0);
  const {
    query,
    setQuery
  } = useQueryString(ResultsQuery.zQueryNoUpgrade);
  let t1;
  if ($[2] !== question_id) {
    t1 = {
      question_id
    };
    $[2] = question_id;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const questionStats = useQuestionStats(t1);
  const {
    tz
  } = usePortal();
  let t2;
  if ($[4] !== question_id) {
    t2 = ["forecast_timeline", question_id];
    $[4] = question_id;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== question_id || $[7] !== tz) {
    t3 = async () => sb.rpc("forecast_timeline", {
      q_id: question_id,
      tz
    }).then(_temp4);
    $[6] = question_id;
    $[7] = tz;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t2 || $[10] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3
    };
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const {
    data: timelinesRaw,
    error,
    isLoading,
    refetch: refetchTimelines
  } = useQuery(t4);
  let t5;
  if ($[12] !== question_id) {
    t5 = {
      filter: {
        question_id
      }
    };
    $[12] = question_id;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const uForecasts = useForecasts(t5);
  let t6;
  if ($[14] !== qForm || $[15] !== question_id) {
    t6 = {
      question_id,
      qForm
    };
    $[14] = qForm;
    $[15] = question_id;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const uForecastNotes = useForecastNotes(t6);
  const publish = useMemoValue(qForm.values?.publish);
  let t7;
  if ($[17] !== publish?.publications) {
    t7 = publish?.publications ?? {};
    $[17] = publish?.publications;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const publications = useMemoValue(t7);
  let noteStats;
  let setTimestamp;
  let t8;
  let timelines;
  let timestamps;
  if ($[19] !== publications || $[20] !== publish?.forecasts || $[21] !== query || $[22] !== setQuery || $[23] !== timelinesRaw || $[24] !== uForecasts.forecastGroups) {
    timestamps = isNullOrEmpty(query?.compare_ts) ? [TsLatest] : query.compare_ts;
    setTimestamp = props => {
      setQuery({
        compare_ts: match(props).with({
          mode: "clear"
        }, _temp5).with({
          mode: "select"
        }, t9 => {
          const {
            ts
          } = t9;
          return unique([...timestamps, ts]);
        }).with({
          mode: "deselect"
        }, t10 => {
          const {
            ts: ts_0
          } = t10;
          return timestamps.filter(f => f != ts_0);
        }).exhaustive()
      });
    };
    const currentForecasts = uForecasts.forecastGroups.map(_temp7).flat();
    const t11 = currentForecasts.length;
    const t12 = currentForecasts.filter(_temp8).length;
    let t13;
    if ($[30] !== publish?.forecasts) {
      t13 = Object.values(publish?.forecasts ?? {}).filter(_temp9);
      $[30] = publish?.forecasts;
      $[31] = t13;
    } else {
      t13 = $[31];
    }
    noteStats = {
      total: t11,
      withNotes: t12,
      notesPublished: t13.length
    };
    timelines = timelinesRaw ? [{
      type: "latest",
      key: "latest",
      ts: D.utc().toMillis(),
      name: "Now",
      selected: timestamps.includes(TsLatest),
      noteStats
    }, ...sortBy([...timelinesRaw.map(_temp10).map(f_4 => ({
      ...f_4,
      key: `${f_4.type}|${f_4.ts}`,
      date: D.fromMillis(f_4.ts),
      selected: timestamps.includes(f_4.ts.toString()),
      ...publications[f_4.ts]
    })), ...timestamps.filter(ts_1 => ts_1 != TsLatest && !timelinesRaw.some(t => t.ts == ts_1)).map(_temp11)], [_temp12, "desc"])] : undefined;
    t8 = timelines?.filter(_temp13);
    $[19] = publications;
    $[20] = publish?.forecasts;
    $[21] = query;
    $[22] = setQuery;
    $[23] = timelinesRaw;
    $[24] = uForecasts.forecastGroups;
    $[25] = noteStats;
    $[26] = setTimestamp;
    $[27] = t8;
    $[28] = timelines;
    $[29] = timestamps;
  } else {
    noteStats = $[25];
    setTimestamp = $[26];
    t8 = $[27];
    timelines = $[28];
    timestamps = $[29];
  }
  const selectedForecasts = t8;
  let t9;
  if ($[32] !== uForecasts.forecastGroups) {
    t9 = pipe(firstBy(uForecasts.forecastGroups.flatMap(_temp15), _temp16, "desc")?.created_at, _temp17);
    $[32] = uForecasts.forecastGroups;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  const latestForecastAt = t9;
  let t10;
  if ($[34] !== questionStored?.cfg.publish || $[35] !== refetchTimelines) {
    t10 = () => {
      if (!questionStored?.cfg.publish) {
        return;
      }
      refetchTimelines();
    };
    $[34] = questionStored?.cfg.publish;
    $[35] = refetchTimelines;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  const t11 = questionStored?.cfg.publish;
  let t12;
  if ($[37] !== t11) {
    t12 = [t11];
    $[37] = t11;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  useEffect(t10, t12);
  let t13;
  if ($[39] !== error || $[40] !== id || $[41] !== isLoading || $[42] !== latestForecastAt || $[43] !== noteStats || $[44] !== publications || $[45] !== qForm || $[46] !== questionStats || $[47] !== questionStored || $[48] !== readonly || $[49] !== selectedForecasts || $[50] !== setTimestamp || $[51] !== timelines || $[52] !== timestamps || $[53] !== tz || $[54] !== uForecastNotes.showAllNotes) {
    t13 = {
      timelines,
      publications,
      selectedForecasts,
      error,
      isLoading,
      id,
      timestamps,
      setTimestamp,
      tz,
      readonly,
      row: questionStored,
      qForm,
      latestForecastAt,
      questionStats,
      noteStats,
      showAllNotes: uForecastNotes.showAllNotes
    };
    $[39] = error;
    $[40] = id;
    $[41] = isLoading;
    $[42] = latestForecastAt;
    $[43] = noteStats;
    $[44] = publications;
    $[45] = qForm;
    $[46] = questionStats;
    $[47] = questionStored;
    $[48] = readonly;
    $[49] = selectedForecasts;
    $[50] = setTimestamp;
    $[51] = timelines;
    $[52] = timestamps;
    $[53] = tz;
    $[54] = uForecastNotes.showAllNotes;
    $[55] = t13;
  } else {
    t13 = $[55];
  }
  return t13;
};
export type UseTimeline = ReturnType<typeof useTimeline>;
export const QuestionTimeline = t0 => {
  const $ = _c(30);
  const {
    timelines,
    error,
    isLoading,
    id,
    timestamps,
    setTimestamp,
    publications,
    tz,
    readonly,
    qForm,
    latestForecastAt,
    showAllNotes
  } = t0;
  let t1;
  if ($[0] !== tz) {
    t1 = <>Publications<span className="text-sm pl-4">{tz} time</span></>;
    $[0] = tz;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      root: "w-full "
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = isLoading ? "gray.1" : undefined;
  let t4;
  if ($[3] !== error || $[4] !== id || $[5] !== isLoading || $[6] !== latestForecastAt || $[7] !== publications || $[8] !== qForm || $[9] !== readonly || $[10] !== setTimestamp || $[11] !== showAllNotes || $[12] !== timelines || $[13] !== timestamps) {
    let t5;
    if ($[15] !== id || $[16] !== latestForecastAt || $[17] !== publications || $[18] !== qForm || $[19] !== readonly || $[20] !== setTimestamp || $[21] !== showAllNotes || $[22] !== timestamps) {
      t5 = f => f.map(r => <TimelineItem key={r.key} {...{
        timestamps,
        setTimestamp,
        qForm,
        readonly,
        r,
        id,
        timelines: f,
        publications,
        latestForecastAt,
        showAllNotes
      }} />);
      $[15] = id;
      $[16] = latestForecastAt;
      $[17] = publications;
      $[18] = qForm;
      $[19] = readonly;
      $[20] = setTimestamp;
      $[21] = showAllNotes;
      $[22] = timestamps;
      $[23] = t5;
    } else {
      t5 = $[23];
    }
    t4 = match({
      timelines,
      error,
      isLoading
    }).with({
      isLoading: true
    }, _temp19).with({
      timelines: P.not(P.nullish).select()
    }, t5).with({
      error: P.not(P.nullish).select()
    }, _temp20).otherwise(_temp21);
    $[3] = error;
    $[4] = id;
    $[5] = isLoading;
    $[6] = latestForecastAt;
    $[7] = publications;
    $[8] = qForm;
    $[9] = readonly;
    $[10] = setTimestamp;
    $[11] = showAllNotes;
    $[12] = timelines;
    $[13] = timestamps;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[24] !== t3 || $[25] !== t4) {
    t5 = <div className="space-y-2 pt-2"><Timeline bulletSize={24} lineWidth={2} className="w-full" color={t3}>{t4}</Timeline></div>;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  let t6;
  if ($[27] !== t1 || $[28] !== t5) {
    t6 = <CardCollapsable title={t1} classNames={t2} variant="card">{t5}</CardCollapsable>;
    $[27] = t1;
    $[28] = t5;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  return t6;
};
export const TimeComparisonSelect = t0 => {
  const $ = _c(87);
  const {
    timelines,
    setTimestamp,
    tz,
    timestamps,
    questionStats
  } = t0;
  const [customTs, setCustomTs] = useState();
  const customDt = customTs ? D.fromMillis(customTs) : undefined;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const combo = useCombobox(t1);
  let T0;
  let T1;
  let T2;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[1] !== combo || $[2] !== setTimestamp || $[3] !== timelines || $[4] !== timestamps) {
    const allTimestamps = unique([TsLatest, ...(timelines ?? []).filter(_temp22).map(_temp23), ...(timestamps ?? []).filter(_temp24)]);
    let t9;
    if ($[15] !== timestamps) {
      t9 = ts_0 => ({
        value: ts_0,
        label: ts_0 == TsLatest ? "Now" : fmtDate(D.fromMillis(Number(ts_0)), {
          showTime: true
        }),
        selected: timestamps?.includes(ts_0) ?? false
      });
      $[15] = timestamps;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    const items = allTimestamps.map(t9);
    const selectedItems = items.filter(_temp25);
    const isDefault = selectedItems.length == 0 || selectedItems.length == 1 && selectedItems[0].value == TsLatest;
    T2 = Combo;
    t4 = combo;
    t5 = "bottom-start";
    t6 = false;
    t7 = v => {
      const value = v as string;
      const isSelected = selectedItems.some(i_0 => i_0.value == value);
      setTimestamp({
        mode: isSelected ? "deselect" : "select",
        ts: value
      });
    };
    let t10;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <Combo.Chevron />;
      $[17] = t10;
    } else {
      t10 = $[17];
    }
    let t11;
    if ($[18] !== combo) {
      t11 = () => combo.toggleDropdown();
      $[18] = combo;
      $[19] = t11;
    } else {
      t11 = $[19];
    }
    let t12;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <span className="text-gray-400 text-sm">as of</span>;
      $[20] = t12;
    } else {
      t12 = $[20];
    }
    const t13 = <PillsInput pointer={true} rightSectionPointerEvents="none" rightSection={t10} onClick={t11}><Pill.Group>{t12}{selectedItems.map(_temp26)}</Pill.Group></PillsInput>;
    let t14;
    if ($[21] !== isDefault || $[22] !== setTimestamp) {
      t14 = !isDefault && <CloseButton className="point m-1" size="sm" onClick={e => {
        e.stopPropagation();
        setTimestamp({
          mode: "clear"
        });
      }} />;
      $[21] = isDefault;
      $[22] = setTimestamp;
      $[23] = t14;
    } else {
      t14 = $[23];
    }
    if ($[24] !== t13 || $[25] !== t14) {
      t8 = <Combo.Target><div className="flex items-center">{t13}{t14}</div></Combo.Target>;
      $[24] = t13;
      $[25] = t14;
      $[26] = t8;
    } else {
      t8 = $[26];
    }
    T1 = Combo.Dropdown;
    t3 = "max-h-[60vh] overflow-y-auto min-w-[20em] flex flex-col gap-3";
    T0 = Combo.Options;
    let t15;
    if ($[27] !== timelines) {
      t15 = i_2 => {
        const isPublished = timelines?.some(t_1 => t_1.ts.toString() == i_2.value && t_1.type == "publish");
        return <Combo.Option key={i_2.value} value={i_2.value} className="cursor-pointer !py-2" onClick={_temp27}><div className="flex gap-2 items-center">{i_2.selected && <CheckIcon size="1em" className="text-gray-400 w-4" />}{isPublished && <FaGlobeAmericas size="1em" className="w-4 text-green-600" />}<div className="font-semibold">{i_2.label}</div></div></Combo.Option>;
      };
      $[27] = timelines;
      $[28] = t15;
    } else {
      t15 = $[28];
    }
    t2 = items.map(t15);
    $[1] = combo;
    $[2] = setTimestamp;
    $[3] = timelines;
    $[4] = timestamps;
    $[5] = T0;
    $[6] = T1;
    $[7] = T2;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    T0 = $[5];
    T1 = $[6];
    T2 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
    t8 = $[14];
  }
  let t9;
  if ($[29] !== T0 || $[30] !== t2) {
    t9 = <T0>{t2}</T0>;
    $[29] = T0;
    $[30] = t2;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  const T3 = CardCollapsable;
  let t10;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="flex items-center gap-2"><HiClock className="" /> Custom date and time</div>;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  const t11 = "card";
  const t12 = true;
  const t13 = "flex flex-col gap-2 pt-2";
  const T4 = DatePicker;
  const t14 = customDt?.toLocal().toISODate();
  let t15;
  if ($[33] !== customDt) {
    t15 = v_0 => {
      const date = v_0 == null ? null : D.fromISO(v_0);
      const localDate = customDt?.toLocal();
      const tsWithTime = date?.set({
        hour: localDate?.hour,
        minute: localDate?.minute
      }).toMillis();
      setCustomTs(tsWithTime);
    };
    $[33] = customDt;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = [];
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = new Date();
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== questionStats?.forecasts_from) {
    t18 = questionStats?.forecasts_from?.toLocal().endOf("day").toISODate();
    $[37] = questionStats?.forecasts_from;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== questionStats?.forecasts_to) {
    t19 = questionStats?.forecasts_to?.toLocal().startOf("day").toISODate();
    $[39] = questionStats?.forecasts_to;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== T4 || $[42] !== t14 || $[43] !== t15 || $[44] !== t18 || $[45] !== t19) {
    t20 = <T4 value={t14} onChange={t15} weekendDays={t16} size="sm" defaultDate={t17} minDate={t18} maxDate={t19} allowDeselect={true} />;
    $[41] = T4;
    $[42] = t14;
    $[43] = t15;
    $[44] = t18;
    $[45] = t19;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== customDt) {
    t21 = customDt?.toLocal().toFormat("HH:mm") ?? "";
    $[47] = customDt;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  const t22 = !customDt;
  let t23;
  if ($[49] !== customDt) {
    t23 = v_1 => {
      if (!customDt) {
        return;
      }
      const {
        hour,
        minute
      } = D.fromISO(v_1.target.value).toObject();
      const newCustomTs = customDt.toLocal().set({
        hour,
        minute
      }).toMillis();
      setCustomTs(newCustomTs);
    };
    $[49] = customDt;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== t21 || $[52] !== t22 || $[53] !== t23) {
    t24 = <TimeInput size="md" variant="filled" value={t21} label="Time" disabled={t22} onChange={t23} />;
    $[51] = t21;
    $[52] = t22;
    $[53] = t23;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  const t25 = !customDt;
  let t26;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = {
      root: "ml-auto"
    };
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] !== combo || $[57] !== customTs || $[58] !== setTimestamp) {
    t27 = () => {
      if (!customTs) {
        return;
      }
      setTimestamp({
        mode: "select",
        ts: customTs.toString()
      });
      setCustomTs(undefined);
      combo.closeDropdown();
    };
    $[56] = combo;
    $[57] = customTs;
    $[58] = setTimestamp;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] !== t25 || $[61] !== t27) {
    t28 = <Button variant="filled" disabled={t25} classNames={t26} onClick={t27}>Add time comparison</Button>;
    $[60] = t25;
    $[61] = t27;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  let t29;
  if ($[63] !== t20 || $[64] !== t24 || $[65] !== t28) {
    t29 = <div className={t13}>{t20}{t24}{t28}</div>;
    $[63] = t20;
    $[64] = t24;
    $[65] = t28;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== T3 || $[68] !== t10 || $[69] !== t29) {
    t30 = <T3 title={t10} variant={t11} startCollapsed={t12}>{t29}</T3>;
    $[67] = T3;
    $[68] = t10;
    $[69] = t29;
    $[70] = t30;
  } else {
    t30 = $[70];
  }
  let t31;
  if ($[71] !== tz) {
    t31 = <div className="opacity-50 border-t pt-2">All times are in {tz} time</div>;
    $[71] = tz;
    $[72] = t31;
  } else {
    t31 = $[72];
  }
  let t32;
  if ($[73] !== T1 || $[74] !== t3 || $[75] !== t30 || $[76] !== t31 || $[77] !== t9) {
    t32 = <T1 className={t3}>{t9}{t30}{t31}</T1>;
    $[73] = T1;
    $[74] = t3;
    $[75] = t30;
    $[76] = t31;
    $[77] = t9;
    $[78] = t32;
  } else {
    t32 = $[78];
  }
  let t33;
  if ($[79] !== T2 || $[80] !== t32 || $[81] !== t4 || $[82] !== t5 || $[83] !== t6 || $[84] !== t7 || $[85] !== t8) {
    t33 = <T2 store={t4} position={t5} withinPortal={t6} onOptionSubmit={t7}>{t8}{t32}</T2>;
    $[79] = T2;
    $[80] = t32;
    $[81] = t4;
    $[82] = t5;
    $[83] = t6;
    $[84] = t7;
    $[85] = t8;
    $[86] = t33;
  } else {
    t33 = $[86];
  }
  return t33;
};
export type SharedPublicationProps = {
  id: string;
  publications: Record<string, QuestionPublicationCfg>;
  variant?: 'button' | 'icon';
  ts?: string;
  query: ResultsQuery.ResultsQuery;
  latestForecastAt: DateTime | undefined;
  noteStats?: {
    notesPublished: number;
    withNotes: number;
  };
  showAllNotes: () => void;
};
export const SharePublication = t0 => {
  const $ = _c(14);
  const {
    id,
    publications,
    variant: t1,
    ts,
    query,
    latestForecastAt,
    noteStats,
    showAllNotes
  } = t0;
  const variant = t1 === undefined ? "button" : t1;
  const [opened, setOpened] = useState(false);
  let t2;
  if ($[0] !== variant) {
    t2 = <Popover.Target>{variant == "icon" ? <ActionIcon className="invisible group-hover:visible" onClick={() => setOpened(_temp28)}><HiShare /></ActionIcon> : <Button leftSection={<HiShare />} onClick={() => setOpened(_temp29)}>Share</Button>}</Popover.Target>;
    $[0] = variant;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== id || $[3] !== latestForecastAt || $[4] !== noteStats || $[5] !== publications || $[6] !== query || $[7] !== showAllNotes || $[8] !== ts) {
    t3 = <Popover.Dropdown className="w-96 max-w-[min(90vw,40em)]"><SharePublicationDropdown {...{
        id,
        publications,
        ts,
        query,
        latestForecastAt,
        noteStats,
        showAllNotes
      }} /></Popover.Dropdown>;
    $[2] = id;
    $[3] = latestForecastAt;
    $[4] = noteStats;
    $[5] = publications;
    $[6] = query;
    $[7] = showAllNotes;
    $[8] = ts;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== opened || $[11] !== t2 || $[12] !== t3) {
    t4 = <Popover opened={opened} onChange={setOpened} position="bottom" withArrow={true} shadow="md">{t2}{t3}</Popover>;
    $[10] = opened;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
};
const SharePublicationDropdown = t0 => {
  const $ = _c(14);
  const {
    id,
    publications,
    ts: tsRaw,
    query,
    latestForecastAt,
    noteStats,
    showAllNotes
  } = t0;
  const ts = tsRaw ?? TsLatest;
  const {
    publishBatch,
    isPending
  } = usePublishBatch();
  const [alwaysLatest, setAlwaysLatest] = useState(false);
  const latestPublishedTs = pipe(entries(publications).filter(_temp30).map(_temp31), _temp33);
  const effectiveTs = ts == TsLatest ? latestPublishedTs : Number.parseInt(ts);
  const effectivePublication = effectiveTs ? publications[effectiveTs.toString()] : undefined;
  let t1;
  if ($[0] !== publications) {
    t1 = t_0 => publications[t_0]?.published == true;
    $[0] = publications;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [compare_ts, compareTsUnpublished] = partition(query?.compare_ts?.filter(t => t !== TsLatest && (ts ? t != ts : true)).map(_temp34) ?? [], t1);
  const hasNewerForecasts = latestForecastAt ? latestPublishedTs ? latestForecastAt.toMillis() > latestPublishedTs : true : false;
  const queryForLink = z.object(ResultsQuery.zQueryShape).strip().parse(compactObj({
    ...query,
    compare_ts: isNullOrEmpty(compare_ts) ? undefined : compare_ts.map(_temp35)
  }));
  const {
    data: shareUrl
  } = usePublishedUrl({
    type: "published",
    id,
    ts: ts != TsLatest ? Number.parseInt(ts) : alwaysLatest ? undefined : latestPublishedTs,
    query: queryForLink
  });
  let t2;
  if ($[2] !== publications) {
    t2 = isEmpty(entries(publications).filter(_temp36));
    $[2] = publications;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const hasPublications = !t2;
  const anyFiltersOrDisplayOptions = notNullOrEmpty(queryForLink);
  const isForecasterCompatible = effectivePublication?.appVersion != null && compare("0.5.0", effectivePublication.appVersion) >= 0;
  const t3 = "flex flex-col gap-4";
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <h3>Share a link to the published results</h3>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== hasPublications || $[6] !== id || $[7] !== noteStats || $[8] !== showAllNotes) {
    t5 = !hasPublications && <>{noteStats && <ForecastNotesWarning noteStats={noteStats} showAllNotes={showAllNotes} />}<Alert color="orange" variant="light" title="Not published yet"><PublishInfoLabel /><PublishButton question_id={id} ts={D.utc().toMillis()} variant="filled" classNames={{
          root: "mt-2 self-end"
        }}>Publish</PublishButton></Alert></>;
    $[5] = hasPublications;
    $[6] = id;
    $[7] = noteStats;
    $[8] = showAllNotes;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = hasPublications && effectiveTs && <><div className="text-sm flex items-center flex-wrap gap-2"><LabelForm>Using publication from:</LabelForm>{publications[effectiveTs.toString()]?.name ?? fmtDate(D.fromMillis(effectiveTs), {
        showTime: true,
        showTz: true
      })}{ts == TsLatest && !hasNewerForecasts && <div className="flex items-center gap-1 text-green-600"><CheckIcon size={16} /><span>includes all the latest forecasts</span></div>}</div>{ts == TsLatest && hasNewerForecasts && <Alert color="orange" variant="light" title="New forecasts"><div>Forecasts have been made since the latest published version. Publish the latest forecasts before if you want to include them.</div><PublishButton question_id={id} ts={DateTime.now().toMillis()} color="orange" variant="filled" classNames={{
        root: "mt-2 self-end"
      }}>Publish</PublishButton></Alert>}{compareTsUnpublished.length > 0 && <Alert color="orange" variant="light"><div><div className="opacity-70">Some time comparisons have not been published and won't be shown. Publish these times first to include them:</div><div className="mt-1 text-sm">{compareTsUnpublished.map(_temp37)}</div></div><Button leftSection={<FaGlobeAmericas />} variant="filled" color="orange" classNames={{
        root: "mt-2 self-end"
      }} onClick={() => publishBatch(id, compareTsUnpublished)} loading={isPending}>Publish missing comparisons</Button></Alert>}{notNullOrEmpty(query.forecasters) && !isForecasterCompatible && <Alert color="orange" variant="light" title="Published version to compatible with filters">Forecast filters not included as this requires a new version of the published report. Update the published version first.<PublishButton question_id={id} ts={effectiveTs} color="orange" variant="filled" classNames={{
        root: "mt-2 self-end"
      }}>Publish</PublishButton></Alert>}{ts == TsLatest && <div className="flex items-center gap-2 justify-between p-2 bg-gray-50 rounded border"><div className="text-sm"><div className="font-medium">Point to latest published version</div><div className="text-gray-600">When enabled, the link below will open/redirect to the most recent published version. If you are embedding in an article, you should leave this option off to avoid stealthy unintentional updates.</div></div><Switch checked={alwaysLatest} onChange={event => setAlwaysLatest(event.currentTarget.checked)} size="md" /></div>}{shareUrl && <CopyLink url={shareUrl} buttonProps={{
      variant: "filled"
    }} />}<div className="flex flex-col gap-2 pt-4">{anyFiltersOrDisplayOptions && <InfoLabel> This link includes the current selected filters and display options</InfoLabel>}</div></>;
  let t7;
  if ($[10] !== t4 || $[11] !== t5 || $[12] !== t6) {
    t7 = <div className={t3}>{t4}{t5}{t6}</div>;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
};
const ForecastNotesWarning = t0 => {
  const $ = _c(14);
  const {
    noteStats,
    showAllNotes
  } = t0;
  if (noteStats.withNotes == 0 || noteStats.notesPublished > 0) {
    return null;
  }
  let t1;
  if ($[0] !== noteStats.notesPublished) {
    t1 = <b>{noteStats.notesPublished}</b>;
    $[0] = noteStats.notesPublished;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== noteStats.withNotes) {
    t2 = <b>{noteStats.withNotes}</b>;
    $[2] = noteStats.withNotes;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div>{t1} / {t2} forecast notes have been selected for inclusion in the published version. In the results forecasts tooltip, select "show in published" to include a forecast note.</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = noteStats.withNotes > 1 ? "s" : "";
  let t5;
  if ($[7] !== noteStats.withNotes || $[8] !== showAllNotes || $[9] !== t4) {
    t5 = <Button className="self-end" variant="filled" color="orange" onClick={showAllNotes}>Show all {noteStats.withNotes} note{t4} in the published version</Button>;
    $[7] = noteStats.withNotes;
    $[8] = showAllNotes;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t5) {
    t6 = <Alert color="orange" title="No forecast notes will be shown"><div className="flex flex-col gap-2">{t3}{t5}</div></Alert>;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
};
const zTimelineQuery = z.object({
  timelineExpand: z.number().optional()
});
const TimelineItem = t0 => {
  const $ = _c(100);
  const {
    id,
    r,
    setTimestamp,
    qForm,
    readonly,
    publications,
    latestForecastAt,
    showAllNotes
  } = t0;
  const {
    profilesUI
  } = usePortal();
  const {
    type,
    ts
  } = r;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["latest", "custom"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [editing, setEditing] = useState(t1.includes(type));
  let t2;
  if ($[1] !== r || $[2] !== type) {
    let t3;
    if ($[4] !== type) {
      t3 = t4 => {
        const {
          date
        } = t4;
        return fmtDate(date, {
          showTime: type != "day"
        });
      };
      $[4] = type;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    t2 = match(r).with({
      type: "latest"
    }, _temp38).otherwise(t3);
    $[1] = r;
    $[2] = type;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const timeTitle = t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = zTimelineQuery.pick({
      timelineExpand: true
    }).extend(pick(ResultsQuery.zQueryShape, ["showQuestions", "forecasters"]));
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    query,
    setQuery
  } = useQueryString(t3);
  let t4;
  if ($[7] !== query.timelineExpand || $[8] !== ts) {
    t4 = () => {
      if (query.timelineExpand == ts) {
        setEditing(true);
      }
    };
    $[7] = query.timelineExpand;
    $[8] = ts;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== query.timelineExpand) {
    t5 = [query.timelineExpand];
    $[10] = query.timelineExpand;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  useEffect(t4, t5);
  let t6;
  if ($[12] !== setQuery) {
    t6 = {
      onSuccess: (_, props) => {
        setQuery({
          timelineExpand: props.ts
        });
      }
    };
    $[12] = setQuery;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const {
    publish,
    unpublish,
    isPending,
    isError,
    error,
    reset
  } = usePublish(t6);
  let t7;
  if ($[14] !== error || $[15] !== isError || $[16] !== reset) {
    t7 = () => isError ? <Alert color="red" title="Failed to publish question" onClose={reset}>{error instanceof Error ? error.message : "An unexpected error occurred"}</Alert> : null;
    $[14] = error;
    $[15] = isError;
    $[16] = reset;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const PublishError = t7;
  let t8;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      marginTop: "1em"
    };
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      color: "#fff"
    };
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== id || $[21] !== r || $[22] !== ts) {
    let t11;
    if ($[24] !== id || $[25] !== ts) {
      t11 = __0 => <PublishStatus id={id} ts={ts} type="published" />;
      $[24] = id;
      $[25] = ts;
      $[26] = t11;
    } else {
      t11 = $[26];
    }
    t10 = match(r).with({
      type: "publish"
    }, t11).with({
      type: "day"
    }, _temp39).with({
      type: "status"
    }, _temp40).with({
      type: "latest"
    }, _temp41).with({
      type: "custom"
    }, _temp42).exhaustive();
    $[20] = id;
    $[21] = r;
    $[22] = ts;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[27] !== t10) {
    t11 = <IconContext.Provider value={t9}>{t10}</IconContext.Provider>;
    $[27] = t10;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== editing) {
    t12 = () => setEditing(!editing);
    $[29] = editing;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== r.cfg || $[32] !== type) {
    t13 = type == "status" ? <QuestionStatusIcon item={{
      md: questionStatusMd[r.cfg.status],
      value: r.cfg.status
    }} /> : undefined;
    $[31] = r.cfg;
    $[32] = type;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== t12 || $[35] !== t13 || $[36] !== timeTitle) {
    t14 = <div className="grow font-semibold flex items-center gap-2 cursor-pointer" onClick={t12}>{timeTitle}{t13}</div>;
    $[34] = t12;
    $[35] = t13;
    $[36] = timeTitle;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== editing || $[39] !== readonly) {
    t15 = !readonly && <ActionIcon className={editing ? "" : "invisible group-hover:visible"} onClick={() => setEditing(!editing)}>{editing ? <HiChevronUp /> : <HiChevronDown />}</ActionIcon>;
    $[38] = editing;
    $[39] = readonly;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  const t16 = r.selected ? "" : "invisible group-hover:visible";
  let t17;
  if ($[41] !== r.selected || $[42] !== setTimestamp || $[43] !== ts || $[44] !== type) {
    t17 = () => setTimestamp({
      mode: r.selected ? "deselect" : "select",
      ts: type == "latest" ? TsLatest : ts.toString()
    });
    $[41] = r.selected;
    $[42] = setTimestamp;
    $[43] = ts;
    $[44] = type;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  const t18 = `${r.selected ? "text-primary" : "text-gray-300"}`;
  let t19;
  if ($[46] !== t18) {
    t19 = <HiEye className={t18} />;
    $[46] = t18;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== t16 || $[49] !== t17 || $[50] !== t19) {
    t20 = <ActionIcon className={t16} onClick={t17}>{t19}</ActionIcon>;
    $[48] = t16;
    $[49] = t17;
    $[50] = t19;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== id || $[53] !== latestForecastAt || $[54] !== publications || $[55] !== query || $[56] !== showAllNotes || $[57] !== ts || $[58] !== type) {
    t21 = type == "publish" && <SharePublication id={id} publications={publications} ts={ts.toString()} variant="icon" query={query} latestForecastAt={latestForecastAt} showAllNotes={showAllNotes} />;
    $[52] = id;
    $[53] = latestForecastAt;
    $[54] = publications;
    $[55] = query;
    $[56] = showAllNotes;
    $[57] = ts;
    $[58] = type;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  let t22;
  if ($[60] !== t15 || $[61] !== t20 || $[62] !== t21) {
    t22 = <div className="flex items-center shrink">{t15}{t20}{t21}</div>;
    $[60] = t15;
    $[61] = t20;
    $[62] = t21;
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  let t23;
  if ($[64] !== t14 || $[65] !== t22) {
    t23 = <div className="flex items-center justify-between  ">{t14}{t22}</div>;
    $[64] = t14;
    $[65] = t22;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  let t24;
  if ($[67] !== editing || $[68] !== profilesUI || $[69] !== qForm || $[70] !== r || $[71] !== type) {
    let t25;
    if ($[73] !== editing || $[74] !== profilesUI || $[75] !== qForm || $[76] !== type) {
      t25 = t26 => {
        const {
          cfg: t27,
          name,
          ts: ts_0
        } = t26;
        const {
          num_forecasts,
          profiles
        } = t27;
        const form = dbFormSubset(qForm, `publish.publications.${ts_0}`);
        return <><div>{editing ? <TextInput placeholder="Custom name" variant="subtitle" className="font-bold" autoFocus={true} value={name ?? ""} onChange={e => form.setFieldValue("name", e.currentTarget.value)} /> : name && <b>{name}</b>}</div><div className="flex flex-wrap gap-2"><SAvatarGroup profiles={profiles.map(p => profilesUI?.[p]).filter(notNull)} /><div>{num_forecasts} forecasts{type == "publish" ? " since prior publication" : ""}</div></div></>;
      };
      $[73] = editing;
      $[74] = profilesUI;
      $[75] = qForm;
      $[76] = type;
      $[77] = t25;
    } else {
      t25 = $[77];
    }
    t24 = match(r).when(isDayOrPublish, t25).with({
      type: "status"
    }, _temp43).otherwise(_temp44);
    $[67] = editing;
    $[68] = profilesUI;
    $[69] = qForm;
    $[70] = r;
    $[71] = type;
    $[72] = t24;
  } else {
    t24 = $[72];
  }
  let t25;
  if ($[78] !== PublishError || $[79] !== editing || $[80] !== id || $[81] !== isPending || $[82] !== publish || $[83] !== r.noteStats || $[84] !== r.published_at || $[85] !== r.type || $[86] !== readonly || $[87] !== showAllNotes || $[88] !== timeTitle || $[89] !== ts || $[90] !== type || $[91] !== unpublish) {
    t25 = !editing || readonly ? <></> : match(type).with("publish", () => <div className="space-y-2"><div className="flex gap-2"><Tooltip label={<TitleTip title="Un-publish" desc="Delete the publication for everyone" />}><Button color="red" variant="filled" loading={isPending} onClick={() => {
            openConfirmModal({
              title: <span>Un-publish?</span>,
              centered: true,
              children: <div className="space-y-2"><p>Are you sure you want to remove the publication of this question at the url{" "}<PublishLink {...{
                    id,
                    ts,
                    type: "published"
                  }} /> ?</p><p>It will no longer be accessible (including embedded article visualizations)</p></div>,
              labels: {
                confirm: "Un-publish",
                cancel: "Cancel"
              },
              confirmProps: {
                color: "red",
                variant: "filled"
              },
              onConfirm: () => unpublish(id, ts)
            });
          }} leftSection={<HiTrash />}>Un-publish</Button></Tooltip><Tooltip label={<TitleTip title="Update the existing publication" desc={<>Same forecasts as before, with the latest configuration.{" "}{r.published_at && <div className="font-semibold">Last updated {fmtDate(r.published_at, {
              showTime: true
            })}{" "}</div>}</>} />}><Button loading={isPending} leftSection={<HiUpload />} onClick={() => publish(id, ts)}>Update</Button></Tooltip></div><PublishError /></div>).otherwise(() => <div className="space-y-2">{r.type == "latest" && <><PublishInfoLabel /><ForecastNotesWarning noteStats={r.noteStats} showAllNotes={showAllNotes} /></>}<Tooltip label={`publish this question with ${type == "latest" ? "the current forecasts" : `forecasts from ${timeTitle}`}`}><Button variant="filled" loading={isPending} onClick={() => publish(id, ts)} leftSection={<FaGlobeAmericas />}>Publish</Button></Tooltip><PublishError /></div>);
    $[78] = PublishError;
    $[79] = editing;
    $[80] = id;
    $[81] = isPending;
    $[82] = publish;
    $[83] = r.noteStats;
    $[84] = r.published_at;
    $[85] = r.type;
    $[86] = readonly;
    $[87] = showAllNotes;
    $[88] = timeTitle;
    $[89] = ts;
    $[90] = type;
    $[91] = unpublish;
    $[92] = t25;
  } else {
    t25 = $[92];
  }
  let t26;
  if ($[93] !== t24 || $[94] !== t25) {
    t26 = <div className="space-y-2">{t24}{t25}</div>;
    $[93] = t24;
    $[94] = t25;
    $[95] = t26;
  } else {
    t26 = $[95];
  }
  let t27;
  if ($[96] !== t11 || $[97] !== t23 || $[98] !== t26) {
    t27 = <Timeline.Item className="group" style={t8} bullet={t11} title={t23}>{t26}</Timeline.Item>;
    $[96] = t11;
    $[97] = t23;
    $[98] = t26;
    $[99] = t27;
  } else {
    t27 = $[99];
  }
  return t27;
};
const PublishInfoLabel = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="opacity-70 font-semibold text-sm">Publish this question with the current forecasts. The report will be available from the internet, but not able to be found via google or other search engines without a direct link.</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const TitleTip = t0 => {
  const $ = _c(7);
  const {
    title,
    desc
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <div className="font-bold">{title}</div>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== desc) {
    t2 = <div className="opacity-80">{desc}</div>;
    $[2] = desc;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <>{t1}{t2}</>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
function _temp(cfg) {
  return pick(cfg, ["publish"]);
}
function _temp2(d) {
  return d;
}
function _temp3(r_0) {
  throw r_0.error;
}
function _temp4(r) {
  return match(r).with({
    data: P.not(P.nullish).select()
  }, _temp2).otherwise(_temp3);
}
function _temp5() {
  return [];
}
function _temp6(f_0) {
  return isEmpty(f_0.cfg.status);
}
function _temp7(fg) {
  return fg.forecasts?.filter(_temp6) ?? [];
}
function _temp8(f_1) {
  return notNullOrEmpty(f_1.cfg.notes);
}
function _temp9(f_2) {
  return f_2.show;
}
function _temp10(f_3) {
  return zTimelineRow.parse(f_3);
}
function _temp11(ts_string) {
  const ts_2 = Number.parseInt(ts_string);
  return {
    type: "custom",
    ts: ts_2,
    date: D.fromMillis(ts_2),
    selected: true,
    key: `publish|${ts_2}`
  } as TimelineCustom;
}
function _temp12(r_1) {
  return r_1.ts;
}
function _temp13(f_5) {
  return f_5.selected;
}
function _temp14(f_6) {
  return !f_6.cfg.deleted;
}
function _temp15(fg_0) {
  return (fg_0.forecasts ?? []).filter(_temp14);
}
function _temp16(f_7) {
  return D.fromISO(f_7.created_at).toMillis();
}
function _temp17(f_8) {
  return f_8 ? D.fromISO(f_8) : undefined;
}
function _temp18(i) {
  return <Timeline.Item key={i} title={<Skeleton w="10em" h="1.5em" />}><Skeleton w="15em" h="1.5em" /></Timeline.Item>;
}
function _temp19() {
  return range(0, 3).map(_temp18);
}
function _temp20(e) {
  return <Alert>Error loading timeline: {e.message}</Alert>;
}
function _temp21() {}
function _temp22(t) {
  return t.type == "publish" || t.type == "custom";
}
function _temp23(t_0) {
  return t_0.ts.toString();
}
function _temp24(ts) {
  return ts != TsLatest;
}
function _temp25(i) {
  return i.selected;
}
function _temp26(i_1) {
  return <Pill key={i_1.value} className="pointer-events-none !max-w-[20em] bg-blue-100 text-blue-800" classNames={{
    label: "truncate"
  }}>{i_1.label}</Pill>;
}
function _temp27(e_0) {
  return e_0.stopPropagation();
}
function _temp28(o) {
  return !o;
}
function _temp29(o_0) {
  return !o_0;
}
function _temp30(t0) {
  const [, cfg] = t0;
  return cfg.published;
}
function _temp31(t0) {
  const [ts_0] = t0;
  return Number.parseInt(ts_0);
}
function _temp32(ts_1) {
  return ts_1;
}
function _temp33(tss) {
  return isEmpty(tss) ? undefined : firstBy(tss, _temp32, "desc");
}
function _temp34(ts_2) {
  return Number.parseInt(ts_2);
}
function _temp35(ts_3) {
  return ts_3.toString();
}
function _temp36(t0) {
  const [, cfg_0] = t0;
  return cfg_0.published;
}
function _temp37(ts_4) {
  return <div key={ts_4} className="ml-2 font-semibold">{fmtDate(D.fromMillis(ts_4), {
      showTime: true
    })}</div>;
}
function _temp38() {
  return "Now";
}
function _temp39() {
  return <HiCalendar />;
}
function _temp40(r_0) {
  const statusMd = questionStatusMd[r_0.cfg.status];
  return <ThemeIcon size={22} radius="xl" color={statusMd.color}><b className="text-sm opacity-70 uppercase">{(statusMd.label ?? r_0.cfg.status)[0]}</b></ThemeIcon>;
}
function _temp41() {
  return <HiClock />;
}
function _temp42() {
  return <HiPencil />;
}
function _temp43(t0) {}
function _temp44() {}